import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, MAIN_SITE_URL } from '@/data/categories';

const languages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const currentLang = languages.find((l) => l.code === (i18n.language || 'en').split('-')[0]) || languages[0];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b-2 border-background-200 bg-white">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-file-list-3-line text-lg" />
          </span>
          <span className="font-heading text-base font-extrabold tracking-tight text-foreground-950">{t('common.brand')}</span>
        </Link>

        <div className="hidden items-center gap-6 lg:flex">
          <Link to="/" className="text-[15px] font-semibold text-foreground-900 transition-colors hover:text-primary-600 whitespace-nowrap">
            {t('common.navHome')}
          </Link>
          <Link to="/usfans-spreadsheet-guide" className="text-[15px] font-semibold text-foreground-900 transition-colors hover:text-primary-600 whitespace-nowrap">
            {t('common.navShoppingGuide')}
          </Link>
          <a href="#categories" className="text-[15px] font-semibold text-foreground-900 transition-colors hover:text-primary-600 whitespace-nowrap">
            {t('common.navResources')}
          </a>
          <a href="#faq" className="text-[15px] font-semibold text-foreground-900 transition-colors hover:text-primary-600 whitespace-nowrap">
            {t('common.navFaq')}
          </a>
        </div>

        <div className="flex items-center gap-2.5">
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 rounded-md border border-background-300 bg-white px-3 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 whitespace-nowrap"
            >
              <span>{currentLang.flag}</span>
              <span className="hidden sm:inline">{currentLang.label}</span>
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 overflow-hidden rounded-lg border border-background-200 bg-white py-1">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => changeLanguage(l.code)}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-background-100 whitespace-nowrap ${
                      l.code === currentLang.code ? 'font-bold text-primary-600' : 'font-semibold text-foreground-800'
                    }`}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden rounded-md bg-primary-500 px-4 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap md:inline-flex"
          >
            {t('common.visitMain')}
          </a>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-950 lg:hidden"
            aria-label="Menu"
          >
            <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200 bg-white px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link to="/" className="flex items-center justify-between rounded-md px-3 py-2.5 text-[15px] font-semibold text-foreground-900 hover:bg-background-100">
              {t('common.navHome')}
              <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
            </Link>
            <Link to="/usfans-spreadsheet-guide" className="flex items-center justify-between rounded-md px-3 py-2.5 text-[15px] font-semibold text-foreground-900 hover:bg-background-100">
              {t('common.navShoppingGuide')}
              <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
            </Link>
            <a href="#categories" className="flex items-center justify-between rounded-md px-3 py-2.5 text-[15px] font-semibold text-foreground-900 hover:bg-background-100">
              {t('common.navResources')}
              <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
            </a>
            <a href="#faq" className="flex items-center justify-between rounded-md px-3 py-2.5 text-[15px] font-semibold text-foreground-900 hover:bg-background-100">
              {t('common.navFaq')}
              <i className="ri-arrow-right-s-line text-lg text-foreground-400" />
            </a>

            <button
              type="button"
              onClick={() => setCatOpen((v) => !v)}
              className="mt-2 flex items-center justify-between rounded-md px-3 py-2.5 text-[15px] font-semibold text-foreground-900 hover:bg-background-100"
            >
              {t('common.navCategories')}
              <i className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform ${catOpen ? 'rotate-180' : ''}`} />
            </button>

            {catOpen && (
              <div className="mt-1 grid grid-cols-2 gap-1.5 px-1 pb-2">
                {categories.map((c) => (
                  <a
                    key={c.id}
                    href={c.href}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-2.5 py-2 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded bg-primary-100 text-primary-700">
                      <i className="ri-arrow-right-up-line text-xs" />
                    </span>
                    {t(`cat.${c.id}`)}
                  </a>
                ))}
              </div>
            )}

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 inline-flex items-center justify-center rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-background-50"
            >
              {t('common.visitMain')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}